// Convention:
// - Everything lowercase is an object
// - Everything uppercase is a string (the route)

import { keys } from '../keys';

export const baseUrl = {
  Dashboard: keys().NEXT_PUBLIC_DASHBOARD_URL,
  Marketing: keys().NEXT_PUBLIC_MARKETING_URL,
  PublicApi: keys().NEXT_PUBLIC_PUBLIC_API_URL
} as const;

export const routes = {
  dashboard: {
    Api: `${baseUrl.Dashboard}/api`,
    auth: {
      changeEmail: {
        Expired: `${baseUrl.Dashboard}/auth/change-email/expired`,
        Index: `${baseUrl.Dashboard}/auth/change-email`,
        Invalid: `${baseUrl.Dashboard}/auth/change-email/invalid`,
        Request: `${baseUrl.Dashboard}/auth/change-email/request`
      },
      Error: `${baseUrl.Dashboard}/auth/error`,
      forgetPassword: {
        Index: `${baseUrl.Dashboard}/auth/forgot-password`,
        Success: `${baseUrl.Dashboard}/auth/forgot-password/success`
      },
      Index: `${baseUrl.Dashboard}/auth`,
      RecoveryCode: `${baseUrl.Dashboard}/auth/recovery-code`,
      resetPassword: {
        Expired: `${baseUrl.Dashboard}/auth/reset-password/expired`,
        Index: `${baseUrl.Dashboard}/auth/reset-password`,
        Request: `${baseUrl.Dashboard}/auth/reset-password/request`,
        Success: `${baseUrl.Dashboard}/auth/reset-password/success`
      },
      SignIn: `${baseUrl.Dashboard}/auth/sign-in`,
      SignUp: `${baseUrl.Dashboard}/auth/sign-up`,
      Totp: `${baseUrl.Dashboard}/auth/totp`,
      verifyEmail: {
        Expired: `${baseUrl.Dashboard}/auth/verify-email/expired`,
        Index: `${baseUrl.Dashboard}/auth/verify-email`,
        Request: `${baseUrl.Dashboard}/auth/verify-email/request`,
        Success: `${baseUrl.Dashboard}/auth/verify-email/success`
      }
    },
    Index: `${baseUrl.Dashboard}/`,
    invitations: {
      AlreadyAccepted: `${baseUrl.Dashboard}/invitations/already-accepted`,
      Index: `${baseUrl.Dashboard}/invitations`,
      Request: `${baseUrl.Dashboard}/invitations/request`,
      Revoked: `${baseUrl.Dashboard}/invitations/revoked`
    },
    onboarding: {
      Index: `${baseUrl.Dashboard}/onboarding`,
      Organization: `${baseUrl.Dashboard}/onboarding/wedding`,
      User: `${baseUrl.Dashboard}/onboarding/user`
    },
    // Public wedding website routes (no auth required)
    publicWebsite: {
      Index: `${baseUrl.Dashboard}/w`,
      Site: `${baseUrl.Dashboard}/w/[websiteSlug]`,
      Rsvp: `${baseUrl.Dashboard}/w/[websiteSlug]/rsvp`,
      Password: `${baseUrl.Dashboard}/w/[websiteSlug]/password`
    },
    // Keep 'organizations' key for backwards compatibility with code references
    organizations: {
      Index: `${baseUrl.Dashboard}/weddings`,
      slug: {
        Budget: `${baseUrl.Dashboard}/weddings/[slug]/budget`,
        Calendar: `${baseUrl.Dashboard}/weddings/[slug]/calendar`,
        Chat: `${baseUrl.Dashboard}/weddings/[slug]/chat`,
        Timeline: `${baseUrl.Dashboard}/weddings/[slug]/timeline`,
        ChoosePlan: `${baseUrl.Dashboard}/weddings/[slug]/choose-plan`,
        Activity: `${baseUrl.Dashboard}/weddings/[slug]/activity`,
        Contacts: `${baseUrl.Dashboard}/weddings/[slug]/contacts`,
        Documents: `${baseUrl.Dashboard}/weddings/[slug]/documents`,
        Home: `${baseUrl.Dashboard}/weddings/[slug]/home`,
        Inbox: `${baseUrl.Dashboard}/weddings/[slug]/inbox`,
        Index: `${baseUrl.Dashboard}/weddings/[slug]`,
        Venues: `${baseUrl.Dashboard}/weddings/[slug]/venues`,
        Vendors: `${baseUrl.Dashboard}/weddings/[slug]/vendors`,
        Tasks: `${baseUrl.Dashboard}/weddings/[slug]/tasks`,
        GuestList: `${baseUrl.Dashboard}/weddings/[slug]/guests`,
        Website: `${baseUrl.Dashboard}/weddings/[slug]/website`,
        Welcome: `${baseUrl.Dashboard}/weddings/[slug]/welcome`,
        settings: {
          account: {
            Index: `${baseUrl.Dashboard}/weddings/[slug]/settings/account`,
            Inbox: `${baseUrl.Dashboard}/weddings/[slug]/settings/account/inbox`,
            Notifications: `${baseUrl.Dashboard}/weddings/[slug]/settings/account/notifications`,
            Profile: `${baseUrl.Dashboard}/weddings/[slug]/settings/account/profile`,
            Security: `${baseUrl.Dashboard}/weddings/[slug]/settings/account/security`
          },
          Index: `${baseUrl.Dashboard}/weddings/[slug]/settings`,
          organization: {
            Billing: `${baseUrl.Dashboard}/weddings/[slug]/settings/wedding/billing`,
            Developers: `${baseUrl.Dashboard}/weddings/[slug]/settings/wedding/developers`,
            General: `${baseUrl.Dashboard}/weddings/[slug]/settings/wedding/general`,
            Index: `${baseUrl.Dashboard}/weddings/[slug]/settings/wedding`,
            Members: `${baseUrl.Dashboard}/weddings/[slug]/settings/wedding/members`,
            Reminders: `${baseUrl.Dashboard}/weddings/[slug]/settings/wedding/reminders`
          }
        }
      }
    }
  },
  marketing: {
    Api: `${baseUrl.Marketing}/api`,
    Blog: `${baseUrl.Marketing}/blog`,
    Careers: `${baseUrl.Marketing}/careers`,
    Contact: `${baseUrl.Marketing}/contact`,
    CookiePolicy: `${baseUrl.Marketing}/cookie-policy`,
    Docs: `${baseUrl.Marketing}/docs`,
    Index: `${baseUrl.Marketing}/`,
    Pricing: `${baseUrl.Marketing}/pricing`,
    PrivacyPolicy: `${baseUrl.Marketing}/privacy-policy`,
    Roadmap: 'https://achromatic.canny.io',
    Story: `${baseUrl.Marketing}/story`,
    TermsOfUse: `${baseUrl.Marketing}/terms-of-use`
  }
} as const;

type ExtractSlugRoutes<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]: T[K] extends string
          ? T[K] extends `${string}[slug]${string}`
            ? T[K]
            : never
          : ExtractSlugRoutes<T[K]>;
      }[keyof T]
    : never;

type OrganizationsSlugRoutes = ExtractSlugRoutes<
  typeof routes.dashboard.organizations.slug
>;

export function replaceOrgSlug(
  route: OrganizationsSlugRoutes,
  slug: string
): string {
  if (route.indexOf('[slug]') === -1) {
    throw new Error(
      `Invalid route: ${route}. Route must contain the placeholder [slug].`
    );
  }

  return route.replace('[slug]', slug);
}

export function getPathname(route: string, baseUrl: string): string {
  return new URL(route, baseUrl).pathname;
}

export function getOrganizationLogoUrl(
  organizationId: string,
  hash: string
): string {
  return `${routes.dashboard.Api}/organization-logos/${organizationId}?v=${hash}`;
}

export function getUserImageUrl(userId: string, hash: string): string {
  return `${routes.dashboard.Api}/user-images/${userId}?v=${hash}`;
}

export function getContactImageUrl(contactId: string, hash: string): string {
  return `${routes.dashboard.Api}/contact-images/${contactId}?v=${hash}`;
}
